import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { isOwner } from 'src/app/common/menu-def.provider';
import { BillingService } from 'src/app/services/billing/billing.service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { AuthCompany } from 'src/models';

@Injectable({
  providedIn: 'root',
})
export class BillingGuard implements CanActivate {
  constructor(
    private store: Store,
    private router: Router,
    private readonly billingService: BillingService,
  ) { }

  public async canActivate() {
    const company = this.store.selectSnapshot(AuthState.company);
    const { subscription } = company;

    if (subscription?.provider === 'paddle') {
      // Check if we should add choosePlan query parameter
      const shouldAddChoosePlan = await this.shouldAddChoosePlan(company);
      const queryParams = shouldAddChoosePlan ? { choosePlan: 'yes' } : {};

      return this.router.createUrlTree(['/new-billing'], { queryParams });
    }

    return true;
  }

  private async shouldAddChoosePlan(company: AuthCompany): Promise<boolean> {
    const gracePeriod = await lastValueFrom(this.billingService.getGracePeriodDays().pipe(take(1)));

    let trialEndedManually = false;
    let customerStatus = null;
    let hasPaymentMethod = false;

    try {
      if (company?.subscription?.provider === 'paddle') {
        const billingDetails = await lastValueFrom(this.billingService.getBillingDetails().pipe(take(1)));
        trialEndedManually = billingDetails?.trialEndedManually;
        customerStatus = billingDetails?.customerStatus;
        hasPaymentMethod = !!billingDetails?.cardLastFourDigits;
      }
    } catch (e) {
      console.error('Error fetching billing details', e);
    }

    const isFreeSubscription = company.subscription?.status === 'free' && !gracePeriod;
    const hasBillingAccess = company?.userSettings?.billingAccess;
    const isPastDue = customerStatus === 'past_due';
    const isCanceled = customerStatus === 'canceled';

    // Check if we should add choosePlan query parameter
    if (isFreeSubscription && (isOwner(company) || hasBillingAccess)) {
      if (company.subscription?.provider === 'paddle' && !trialEndedManually) {
        return (isPastDue && !hasPaymentMethod) || (isCanceled && hasPaymentMethod);
      }
    }

    return false;
  }
}
